import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Space,
  Button,
  Select,
  DatePicker,
  Typography,
  App,
  Tooltip,
} from 'antd';
import {
  FileExcelOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { getClientRankingVideos, listRanking } from '../../services/competitions';
import {
  listModalities,
  listGenders,
  listCompetitionRegions,
  listCompetitions,
} from '../../services/catalogs';
import { exportToExcel } from '../../utils/excel';
import dayjs from 'dayjs';
import "dayjs/locale/es";
import usePermissions from '../../hooks/usePermissions';

dayjs.locale("es");


const { Title } = Typography;
const { useApp } = App;

const CompetitionRankingList = () => {
  const { message } = useApp();
  const { can } = usePermissions();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});

  // filtros aplicados
  const [modalityId, setModalityId] = useState(null);
  const [genderId, setGenderId] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [regionId, setRegionId] = useState(null);
  const [competitionId, setCompetitionId] = useState(null);

  // filtros draft
  const [draftModality, setDraftModality] = useState(null);
  const [draftGender, setDraftGender] = useState(null);
  const [draftStartDate, setDraftStartDate] = useState(null);
  const [draftEndDate, setDraftEndDate] = useState(null);
  const [draftRegion, setDraftRegion] = useState(null);
  const [draftCompetition, setDraftCompetition] = useState(null);

  // catálogos
  const [modalities, setModalities] = useState([]);
  const [genders, setGenders] = useState([]);
  const [regions, setRegions] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loadingRowKey, setLoadingRowKey] = useState(null);


  useEffect(() => {
    Promise.all([
      listModalities(),
      listGenders(),
      listCompetitionRegions(),
      listCompetitions(),
    ]).then(([m, g, r, c]) => {
      setModalities(m || []);
      setGenders(g || []);
      setRegions(r || []);
      setCompetitions(c || []);
    });
  }, []);

  const fetchData = useCallback(
    async (
      page,
      pageSize,
      srt,
      mId,
      gId,
      sd,
      ed,
      rId,
      cId
    ) => {
      setLoading(true);
      try {
        const params = {
          page: page || pagination.current,
          pageSize: pageSize || pagination.pageSize,
          sortField: srt?.field,
          sortOrder: srt?.order,
          ...(mId && { modalityId: mId }),
          ...(gId && { genderId: gId }),
          ...(sd && { startDate: sd }),
          ...(ed && { endDate: ed }),
          ...(rId && { regionId: rId }),
          ...(cId && { competitionId: cId }),
        };

        const { items, total } = await listRanking(params);

        setData(items || []);
        setPagination(prev => ({
          ...prev,
          current: page || prev.current,
          pageSize: pageSize || prev.pageSize,
          total: total || 0,
        }));
      } catch {
        message.error('No se pudieron cargar los ganadores');
      } finally {
        setLoading(false);
      }
    },
    [pagination, message]
  );

  useEffect(() => {
    fetchData(1, pagination.pageSize, null, null, null, null, null, null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = ({
    modality = null,
    gender = null,
    startDate = null,
    endDate = null,
    region = null,
    competition = null
  } = {}) => {
    setModalityId(modality);
    setGenderId(gender);
    setStartDate(startDate);
    setEndDate(endDate);
    setRegionId(region);
    setCompetitionId(competition);

    fetchData(
      1,
      pagination.pageSize,
      sorter,
      modality,
      gender,
      startDate,
      endDate,
      region,
      competition
    );
  };

  const handleSearch = () => {
    applyFilters({
      modality: draftModality,
      gender: draftGender,
      startDate: draftStartDate,
      endDate: draftEndDate,
      region: draftRegion,
      competition: draftCompetition,
    });
  };

  const handleClearFilters = () => {
    setDraftModality(null);
    setDraftGender(null);
    setDraftStartDate(null);
    setDraftEndDate(null);
    setDraftRegion(null);
    setDraftCompetition(null);
    applyFilters();
  };

  const onTableChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(
      pag.current,
      pag.pageSize,
      srt,
      modalityId,
      genderId,
      startDate,
      endDate,
      regionId,
      competitionId
    );
  };

  const toggleExpand = async (record) => {
    const key = record.clientId;

    // Si ya está expandido → colapsar
    if (expandedRowKeys.includes(key)) {
      setExpandedRowKeys(prev => prev.filter(k => k !== key));
      return;
    }

    try {
      setLoadingRowKey(key);

      // Solo consulta si aún no tiene videos cargados
      if (!record.videos) {
        const items = await getClientRankingVideos(key);
        const videos = items.items ?? undefined;
        setData(prev =>
          prev.map(item =>
            item.clientId === key
              ? { ...item, videos }
              : item
          )
        );
      }

      setExpandedRowKeys(prev => [...prev, key]);
    } catch {
      message.error('No se pudieron cargar los videos');
    } finally {
      setLoadingRowKey(null);
    }
  };


  const expandedRowRender = (record) => {
    if (loadingRowKey === record.clientId) {
      return <div style={{ textAlign: 'center' }}>Cargando...</div>;
    }

    if (!record.videos?.length) {
      return <div style={{ textAlign: 'center' }}>Sin videos</div>;
    }

    return (
      <Table
        rowKey={(r, index) => `${record.clientId}-${index}`}
        pagination={false}
        scroll={{ x: 'max-content' }}
        dataSource={[...record.videos].sort(
          (a, b) => a.position - b.position || b.totalScore - a.totalScore
        )}
        columns={[
          {
            title: 'Video',
            dataIndex: 'video',
            align: 'center',
            render: (_, r) => _?.urlVideo?.split('/').pop() || '—'
          },
          {
            title: 'Competición',
            dataIndex: 'competition',
            align: 'center',
            render: (_, r) => _?.folio || '-'
          },
          {
            title: 'Puntos',
            align: 'center',
            dataIndex: 'totalScore',
          },
        ]}
      />
    );
  };


  const columns = [
    {
      title: 'Puesto',
      dataIndex: 'rank',
      align: 'center',
    },
    {
      title: 'Partcipante',
      dataIndex: 'participant',
      align: 'center',
    },
    {
      title: 'Modalidad',
      dataIndex: 'modality',
      align: 'center',
    },

    {
      title: 'Competencia',
      dataIndex: 'competition',
      align: 'center',
    },

    {
      title: 'Género',
      dataIndex: 'gender',
      align: 'center',
    },
    {
      title: 'Región',
      dataIndex: 'region',
      align: 'center',
    },
    {
      title: 'Puntos Totales',
      align: 'center',
      dataIndex: 'totalPoints',
    },
  ];

  const handleExportToExcel = async () => {
    setExporting(true);

    try {
      const { items } = await listRanking({
        page: 1,
        pageSize: 1000,
        modalityId,
        genderId,
        startDate,
        endDate,
        regionId,
        competitionId,
        sortField: sorter?.field,
        sortOrder: sorter?.order,
      });

      if (!items?.length) {
        message.warning('No hay información para exportar');
        return;
      }

      const excelData = [];

      items.forEach((competition) => {
        // Fila principal
        excelData.push({
          Puesto: competition.rank,
          Participante: competition.participant || '-',
          Modalidad: competition.modality || '-',
          Competencia: competition.competition || '-',
          Región: competition.region || '-',
          'Puntos Totales': competition.totalPoints || 0,
        });
      });

      exportToExcel(
        excelData,
        [],
        'Ranking',
        'Ranking'
      );
    } catch {
      message.error('No se pudo exportar el archivo');
    } finally {
      setExporting(false);
    }
  };

  const buildRankingTitle = () => {
    const formatDate = (date) =>
      date ? dayjs(date).format('D [de] MMMM YYYY') : null;

    const getLabel = (id, list, defaultValue = 'Todas') => {
      if (!id) return defaultValue;
      const found = list.find(item => item.id === id);
      return found?.name || found?.description || found?.code || defaultValue;
    };

    const start = formatDate(startDate);
    const end = formatDate(endDate);

    const dateText =
      start || end
        ? `${start || '-'} a ${end || ''}`
        : 'Todas las fechas';

    return `Ranking: ${dateText} | 
  Categoría ${getLabel(modalityId, modalities, 'Todas')} | 
  Competencia ${getLabel(competitionId, competitions, 'Todas')} | 
  Género ${getLabel(genderId, genders, 'Todos')} | 
  Región ${getLabel(regionId, regions, 'Todas')}`;
  };


  const rankingTitle = buildRankingTitle();

  return (
    <div style={{ padding: 24 }}>
      <div className="header-container">
        <Title level={3} style={{ marginBottom: 0 }}>Ganadores</Title>
        <Space>
          {can('competitions.export') && (<Tooltip title="Exportar a Excel">
              <Button
                className='btn-add'
                icon={<FileExcelOutlined />}
                onClick={handleExportToExcel}
                loading={exporting}
                disabled={exporting}
                aria-label="Exportar a Excel"
              />
          </Tooltip>)}
        </Space>
      </div>
      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <DatePicker
            placeholder="Inicio"
            value={draftStartDate ? dayjs(draftStartDate, 'YYYY-MM-DD') : null}
            onChange={(_, d) => setDraftStartDate(d)}
          />
          <DatePicker
            placeholder="Fin"
            value={draftEndDate ? dayjs(draftEndDate, 'YYYY-MM-DD') : null}
            onChange={(_, d) => setDraftEndDate(d)}
          />
          <Select
            placeholder="Modalidad"
            allowClear
            style={{ width: 160 }}
            value={draftModality}
            onChange={setDraftModality}
            options={modalities.map(m => ({
              value: m.id,
              label: m.name || m.code,
            }))}
          />
          <Select
            placeholder="Competencia"
            allowClear
            style={{ width: 160 }}
            value={draftCompetition}
            onChange={setDraftCompetition}
            options={competitions.map(p => ({
              value: p.id,
              label: p.description || p.code,
            }))}
          />
          <Select
            placeholder="Género"
            allowClear
            style={{ width: 160 }}
            value={draftGender}
            onChange={setDraftGender}
            options={genders.map(g => ({
              value: g.id,
              label: g.description || g.code,
            }))}
          />
          <Select
            placeholder="Región"
            allowClear
            style={{ width: 160 }}
            value={draftRegion}
            onChange={setDraftRegion}
            options={regions.map(p => ({
              value: p.id,
              label: p.description || p.code,
            }))}
          />
          <Tooltip title="Buscar">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
            />
          </Tooltip>
          <Tooltip title="Limpiar filtros">
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
            />
          </Tooltip>
        </Space>
        <h3
          style={{
            color: "#BA955E",
            fontWeight: 600,
            marginBottom: "20px",
          }}
        >
          {rankingTitle}
        </h3>

        <Table
          rowKey="clientId"
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 'max-content' }}
          expandable={{
            expandedRowKeys,
            expandedRowRender,
            rowExpandable: () => true,
            expandIcon: () => null,
          }}
          onRow={(record) => ({
            onClick: () => toggleExpand(record),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
            showTotal: t => `Total: ${t} competencias`,
          }}
          onChange={onTableChange}
        />
      </Card>
    </div>
  );
};

export default CompetitionRankingList;
